import {useState, useEffect} from 'react'

import { GridColDef, useGridApiRef } from '@mui/x-data-grid';

import toastr from 'toastr';

import { projectAPI } from '../../../api';
import { StyledDataGrid, dataGridCellClassName, dataGridHeaderClassName } from '../../../utils'

import {Modal} from '../../../components/Modal'

interface Props {
  projectId: string
  name: string
  show: boolean
  handleClose: () => void
  handleUpdated?: () => void
}

const EditParameters = ({projectId, id, useOwnParameters, parameters}) =>{
  
  const [textareaValue, setTextareaValue] = useState('')

  useEffect(() => {
    setTextareaValue(parameters ? JSON.stringify(parameters) : '')
  }, [parameters])

  const handleTextareaChange = (e) => {
    setTextareaValue(e.target.value)
  }

  const handleUpdate = () => {
    if( textareaValue && isValidDictWithPossibleKeys(textareaValue) ){
      projectAPI.updateSetting(projectId, 'model', {'attribute_name': 'parameters', 'attribute_value': JSON.parse(textareaValue), 'id':id})
      toastr.info('Updated')
    }
  }
  
  const isValidDictWithPossibleKeys = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      if (typeof parsed === 'object' && parsed !== null) {
        const possibleKeys = ['system_message', 'max_tokens', 'temperature', 'frequency_penalty', 'repetition_penalty'];
        const parsedKeys = Object.keys(parsed);

        const hasOnlyPossibleKeys = parsedKeys.every(key => possibleKeys.includes(key))

        if (hasOnlyPossibleKeys) {
          return true
        } else {
          const invalidKeys = parsedKeys.filter(key => !possibleKeys.includes(key))
          toastr.error(`Allowed parameters: ${possibleKeys.join(", ")}`)
          return false
        }
      } else {
        toastr.error('Input is not a valid object.')
        return false
      }
    } catch (e) {
      toastr.error('Invalid JSON format.')
      return false
    }
  };

  return (
    <>
      
      {useOwnParameters || projectId == 'default' ?
      <>
        <textarea 
          className="form-control" 
          value={textareaValue} 
          onChange={handleTextareaChange} // Capture the textarea input
          rows={3} 
        />
        <button 
          className="btn btn-sm px-2 py-1 btn-light-dark cursor-pointer" 
          onClick={handleUpdate}
        >
          Update
        </button>
      </>
      :
      parameters != null && <pre>{JSON.stringify(parameters, null, 2)}</pre>
      }
    </>
  );
}

const ModelsModal : React.FC<Props> = ({projectId, name, show, handleClose, handleUpdated}) => {

  const [loaded, setLoaded] = useState<boolean>(false)
  const [defaultModels, setDefaultModels] = useState<any[]>([])
  const [models, setModels] = useState<any[]>([])
  const [modelId, setModelId] = useState<number | null>()
  const apiRef = useGridApiRef();

  useEffect(() => {
    if( show ){
      load()
    }
  }, [show])

  async function load(){
    let {data: data} = await projectAPI.getModels(projectId)
    setModels(data['data'])
    let {data: defaultData} = await projectAPI.getModels('default')
    setDefaultModels(defaultData['data'])
    setLoaded(true)
  }


  async function updateSetting(id:number, attribute_name: string, attribute_value: boolean){
    projectAPI.updateSetting(projectId, 'model', {'attribute_name': attribute_name, 'attribute_value': attribute_value, 'id':id})
    if( attribute_name == 'is_default' && attribute_value ){
      models.forEach(model => {
        if( id !== model.id && attribute_value && model.is_default ){
          projectAPI.updateSetting(projectId, 'model', {'attribute_name': attribute_name, 'attribute_value': false, 'id':model.id})
        }
      });
      setModels(models.map((item) => {
        if (item.id === id) {
          return { ...item, [attribute_name]: attribute_value }
        }else{
          return { ...item, [attribute_name]: false }
        }
      }));
    }else{
      setModels(models.map((item) => {
        if (item.id === id) {
          return { ...item, [attribute_name]: attribute_value }
        }
        return item
      }));
    }
  }

  const columns: GridColDef[] = [
    { field: 'model_type', headerName: 'Model Endpoints/Models', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 300, flex:1,
      renderCell: (params) => {
        return <>
        {params.value}
        </>
      }
    },
    { field: 'is_default', headerName: 'Default', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 200,
      renderCell: (params) => {
        return <div className="form-check form-switch form-check-custom">
          <input 
            className="form-check-input h-20px w-30px" 
            type="checkbox"
            checked={params.value}
            onChange={() => {
              updateSetting(params.row.id, 'is_default', !params.value)
            }}
          />
        </div>
      }
    },
    { field: 'context_length', headerName: 'Context length', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 200,
      renderCell: (params) => {
        return <>
        <input 
          className="form-control" 
          type="number"
          value={params.value}
          onChange={(e) => {
            setModels(models.map((item) => {
              if (item.id === params.row.id) {
                return { ...item, 'context_length': e.currentTarget.value }
              }
              return item
            }));
          }}
        />
        <button 
          className="btn btn-sm px-2 py-1 btn-light-dark cursor-pointer" 
          onClick={() => {
            projectAPI.updateSetting(projectId, 'model', {'attribute_name': "context_length", 'attribute_value': params.value, 'id':params.row.id})
          }}
        >
          Update
        </button>
        </>
      }
    },
    { field: 'use_own_parameters', headerName: 'Use own parameters', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 200,
      renderCell: (params) => {
        return <div className="form-check form-switch form-check-custom">
          <input 
            className="form-check-input h-20px w-30px" 
            type="checkbox" 
            checked={params.value}
            onChange={() => {
              updateSetting(params.row.id, 'use_own_parameters', !params.value)
            }}
          />
        </div>
      }
    },
    { field:'parameters', headerName: 'Parameters', headerClassName: dataGridHeaderClassName, cellClassName: dataGridCellClassName, minWidth: 250, flex:2,
      renderCell: (params) => {
        let parameters = params.row.use_own_parameters ? params.row.parameters : defaultModels.find((model) => model.model_type == params.row.model_type)?.parameters;
        return <EditParameters projectId={projectId} id={params.row.id} useOwnParameters={params.row.use_own_parameters} parameters={parameters}/>
      }
    }
  ]


  const columnVisibilities = {
    is_default: projectId == 'default',
    context_length: projectId == 'default',
    use_own_parameters: projectId != 'default',
    created_dt: projectId != 'default',
    deleted_dt: projectId != 'default'
  }


  return <Modal show={show} handleClose={handleClose} dialogClassName="modal-xl" title={'Default parameters'}>
    {loaded && (
      <StyledDataGrid
        apiRef={apiRef}
        getRowId={(row) => row.id}
        rows={models}
        columns={columns}
        disableColumnMenu={false}
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 5 },
          },
          columns: {
            columnVisibilityModel:columnVisibilities
          }
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        pageSizeOptions={[5, 10]}
      />
    )}
  </Modal>
}

export {ModelsModal}